import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useEffect, useMemo, useState } from "react";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Societe from "../../services/Societe";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import SellingService from "../../services/SellingService";
import BDCNonConfirme from "./Table/BDCNonConfirme";
import withLoader from "../../components/layouts/WithLoader";

const animatedComponents = makeAnimated();
const ALL_CLIENTS = { value: 0, label: "Tous" };

const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const BDCSellUpdate = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(ALL_CLIENTS);
  const [dateBeginRaw, setDateBeginRaw] = useState("");
  const [dateEndRaw, setDateEndRaw] = useState("");
  const [inConfirmedBdc, setInConfirmedBdc] = useState([]);
  const [chargedBDCInfo, setChargedBDCInfo] = useState(false);
  const [alert, setAlert] = useState({ open: false, text: "" });

  const showError = (text) => setAlert({ open: true, text });
  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((a) => ({ ...a, open: false }));
  };

  const loadLastMonth = () => {
    startLoading();
    SellingService.getBDCSellOfLastMonthOptimized(getCurrentUserId(), 0)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setInConfirmedBdc(response.data.success);
        }
        setChargedBDCInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  useEffect(() => {
    Societe.getSocietesClients("CLIENT", "CLIENT", 1, 0)
      .then((reponse) => setClients(reponse.data["Societe"] || []))
      .catch(() => showError("Erreur : il y a un problème pour obtenir les clients"));

    loadLastMonth();
  }, []);

  const clientOptions = useMemo(
    () => [
      ALL_CLIENTS,
      ...clients
        .filter((c) => c?.id !== undefined)
        .map((c) => ({ value: c.id, label: c.title })),
    ],
    [clients]
  );

  // Backend expects "any" when no date, and a full timestamp otherwise.
  const dateBegin = dateBeginRaw ? `${dateBeginRaw}T00:00:00` : "any";
  const dateEnd = dateEndRaw ? `${dateEndRaw}T23:59:59` : "any";

  const onGetBdcByFiltersHandler = () => {
    if (dateBeginRaw && dateEndRaw && dateBeginRaw > dateEndRaw) {
      showError("La date de début doit être antérieure à la date de fin");
      return;
    }
    startLoading();
    SellingService.getBDCByFiltersOptimized(
      getCurrentUserId(),
      selectedClient.value,
      0,
      dateBegin,
      dateEnd
    )
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setInConfirmedBdc(response.data.success);
        } else {
          setInConfirmedBdc([]);
        }
        setChargedBDCInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  const onResetFilters = () => {
    setSelectedClient(ALL_CLIENTS);
    setDateBeginRaw("");
    setDateEndRaw("");
    loadLastMonth();
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-edit mr-2"></i>Modifier les bons de commande de vente
              </h4>
              <small className="text-muted">
                Par défaut, les bons de commande ajoutés durant le dernier mois
              </small>
            </div>
            <button
              className="btn btn-outline-secondary mt-2 mt-sm-0"
              onClick={() => navigate("/bonCommandeVente")}
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Retour aux bons de commande
            </button>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-dark mt-3" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Filtres
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label style={labelStyle}>Client</label>
                    <Select
                      closeMenuOnSelect={true}
                      components={animatedComponents}
                      value={selectedClient}
                      onChange={setSelectedClient}
                      options={clientOptions}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="bdcUpdDateBegin" style={labelStyle}>
                      Date début
                    </label>
                    <input
                      id="bdcUpdDateBegin"
                      type="date"
                      className="form-control"
                      value={dateBeginRaw}
                      max={dateEndRaw || undefined}
                      onChange={(e) => setDateBeginRaw(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="bdcUpdDateEnd" style={labelStyle}>
                      Date fin
                    </label>
                    <input
                      id="bdcUpdDateEnd"
                      type="date"
                      className="form-control"
                      value={dateEndRaw}
                      min={dateBeginRaw || undefined}
                      onChange={(e) => setDateEndRaw(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-right">
              <button className="btn btn-outline-dark mr-2" onClick={onResetFilters}>
                <i className="fas fa-undo mr-1"></i> Réinitialiser
              </button>
              <button className="btn btn-dark" onClick={onGetBdcByFiltersHandler}>
                <i className="fas fa-search mr-1"></i> Valider le choix
              </button>
            </div>
          </div>

          {chargedBDCInfo && <BDCNonConfirme listBDC={inConfirmedBdc} />}
        </div>
      </section>

      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleAlertClose} severity="error">
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default withLoader(BDCSellUpdate);
